import { Box, type SxProps, type Theme, Typography } from '@mui/material';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { snackbarColor } from '@/enums';
import { useSnackbar } from '@/hooks';
import { useCheckFileType } from '@/hooks/useCheckFileType';
import { cn } from '@/lib/utils';
import { wordsCap } from '@/utils/helpers';
import { UploadFieldText, UploadFieldWrap } from './UploadField.styles';

interface IUploadField {
    value: string | null;
    accept?: string;
    onChange: (file: File | null) => void;
    uploadFormat?: string[];
    uploadMaxSize?: number;
    label?: string;
    sx?: SxProps<Theme>;
    mini?: boolean;
}

export const UploadField = ({ uploadFormat, accept, uploadMaxSize, onChange, value, label, sx, mini }: IUploadField) => {
    const { t } = useTranslation();
    const uploadRef = useRef<HTMLInputElement>(null);
    const showSnackbar = useSnackbar();
    const { checkFileDocx } = useCheckFileType();

    const formatInfo = uploadFormat && t('upload-format', { format: uploadFormat.join(', ') });
    const sizeInfo = uploadMaxSize && t('upload-maxsize', { size: uploadMaxSize });

    const changeCallback = (file: File | null) => {
        onChange(file);
        if (uploadRef?.current) {
            uploadRef.current.value = '';
        }
    };

    const handleChange = () => {
        if (uploadRef?.current?.files) {
            const uploadedFile = uploadRef.current.files[0];
            if (!uploadedFile) return;

            // Checking file size by props >>
            if (uploadMaxSize) {
                if (uploadedFile.size / 1e6 > uploadMaxSize) {
                    showSnackbar({
                        open: true,
                        message: t('verifyed-upload', { size: uploadMaxSize }),
                        severity: snackbarColor.error,
                    });
                    changeCallback(null);
                    return;
                }
            }
            // << Checking file size by props

            // Checking format file. Working with docx only! >>
            if (uploadFormat && uploadFormat.length === 1 && uploadFormat[0]?.toLowerCase() === 'docx') {
                if (!checkFileDocx(uploadedFile)) {
                    changeCallback(null);
                    return;
                }
            }
            // << Checking format file. Working with docx only!

            changeCallback(uploadedFile);
        }
    };

    return (
        <Box display="grid" gap="5px" sx={sx}>
            <Typography
                sx={{ color: theme => (theme.palette.mode === 'dark' ? 'primary.contrastText' : theme.custom.c79) }}
                children={label || t('upload-file')}
            />
            <UploadFieldWrap mini={mini}>
                <input ref={uploadRef} accept={accept} type="file" id="importScript" onChange={handleChange} hidden />
                {!mini && (
                    <UploadFieldText component="label" htmlFor="importScript" children={<Text variant="subtitle2" children={value} />} />
                )}
                <Button asChild variant="outlined" size="small" className={cn(mini && 'min-w-[120px]')}>
                    <label htmlFor="importScript">{t(value ? 'buttons.replace' : 'upload')}</label>
                </Button>
            </UploadFieldWrap>
            {(formatInfo || sizeInfo) && !mini && (
                <Typography variant="body2" sx={theme => ({ color: theme.custom.c5 })}>
                    {formatInfo && formatInfo}
                    {sizeInfo && (formatInfo ? `, ${sizeInfo}` : wordsCap(sizeInfo))}
                </Typography>
            )}
        </Box>
    );
};
